import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Loader2, Check, Landmark } from 'lucide-react';
import { putSettings } from './api';

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
  </label>
);

export const CfoSettingsDialog = ({ open, onOpenChange, meta, token, onSaved }) => {
  const s = meta?.settings || {};
  const [ob, setOb] = useState('');
  const [obDate, setObDate] = useState('');
  const [taxPct, setTaxPct] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setOb(s.opening_balance ?? '');
      setObDate(s.opening_balance_date || new Date().toISOString().slice(0, 10));
      setTaxPct(s.tax_reserve_pct != null ? Math.round(s.tax_reserve_pct * 100) : 25);
    }
  }, [open]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      await putSettings(token, {
        opening_balance: Number(ob || 0),
        opening_balance_date: obDate || null,
        tax_reserve_pct: Number(taxPct || 0) / 100,
      });
      toast.success('Settings saved');
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cfo-settings-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Landmark className="w-5 h-5" /> CFO Settings</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label={`Opening cash balance (${s.currency || 'AUD'})`} hint="Your cash on hand at the date below. Everything since then is added/subtracted automatically.">
            <Input type="number" value={ob} onChange={(e) => setOb(e.target.value)} placeholder="e.g. 184000" data-testid="settings-opening-balance" autoFocus />
          </Field>
          <Field label="As at date" hint="Transactions dated on or after this count toward your live cash figure.">
            <Input type="date" value={obDate} onChange={(e) => setObDate(e.target.value)} data-testid="settings-ob-date" />
          </Field>
          <Field label="Tax reserve (%)" hint="Suggested % of profit to set aside for tax.">
            <Input type="number" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} placeholder="25" data-testid="settings-tax-pct" />
          </Field>
          <Button onClick={save} disabled={busy} className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-700" data-testid="settings-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1.5" /> Save settings</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CfoSettingsDialog;
